import { useEffect, useState } from "react";

/**
 * Debounces a search input value to reduce API calls and improve performance.
 *
 * Returns a debounced version of the input value that only updates after the
 * specified delay has passed since the last change. Useful for search inputs
 * where you want to wait for the user to finish typing before triggering an
 * API query. Automatically cleans up timers on component unmount or when
 * dependencies change.
 *
 * @param value - The search string value to debounce
 * @param delay - Delay in milliseconds to wait after last change (default: 500ms)
 * @returns The debounced value, updated only after the delay period
 *
 * @example
 * const [search, setSearch] = useState("");
 * const debouncedSearch = useDebouncedSearch(search, 300);
 * // Use debouncedSearch in your query to avoid excessive API calls
 * const { data } = useQuery({ variables: { search: debouncedSearch } });
 */
export function useDebouncedSearch(value: string, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}
